import React, { useEffect, useState } from "react";
import { translateOne } from "@/lib/translate";
import { useApp } from "@/AppContext";
import { SKILL_TRANSLATIONS } from "@/i18n";

/** Turn a snake_case backend code into a human label: "fence_install" → "Fence Install" */
export function humanize(key) {
  if (!key) return "";
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * <TagLabel kind="skill" | "industry" | "employment" code="construction" />
 *
 * Renders a localized label for a backend-defined code.
 * Lookup order:
 *   1. Static i18n map (instant, manual translations curated for slang/idiom).
 *   2. Fallback: humanize the key, send through /translate/batch (cached).
 *
 * This way every existing code stays curated (e.g. "construction" → "Construcción"),
 * but any NEW backend code added later still renders translated text instead of
 * showing raw snake_case to users.
 */
export default function TagLabel({ kind, code, className = "", as = "span" }) {
  const { t, lang, authHeaders } = useApp();
  const staticMap = (() => {
    if (kind === "skill") {
      return SKILL_TRANSLATIONS[lang] || SKILL_TRANSLATIONS.en || {};
    }
    if (kind === "industry") return t?.industries || {};
    if (kind === "employment") return t?.jobs || {};
    return {};
  })();
  const staticLabel = staticMap[code];
  const [resolved, setResolved] = useState(staticLabel || humanize(code));

  useEffect(() => {
    if (staticLabel) {
      setResolved(staticLabel);
      return;
    }
    // Not in the static map → humanize then translate to viewer's language
    const human = humanize(code);
    if (lang === "en") {
      setResolved(human);
      return;
    }
    let cancelled = false;
    (async () => {
      const t2 = await translateOne(lang, human, authHeaders());
      if (!cancelled) setResolved(t2);
    })();
    return () => { cancelled = true; };
  }, [code, lang, staticLabel, authHeaders]);

  const Tag = as;
  return <Tag className={className} data-testid={`tag-${kind}-${code}`}>{resolved}</Tag>;
}
